/**
 * Hook for Movimientos Territoriales (transfers) report
 */

import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { getMinisterTransfers } from "../services/reports.service";
import type { MinisterTransferFilters, TransferDistrictFlow } from "../types";

const CURRENT_YEAR = new Date().getFullYear();

export function getDefaultTransferFilters(): MinisterTransferFilters {
  return {
    dateFrom: `${CURRENT_YEAR}-01-01`,
    dateTo: `${CURRENT_YEAR}-12-31`,
  };
}

export function useMinisterTransfersReport() {
  const [filters, setFilters] = useState<MinisterTransferFilters>(
    getDefaultTransferFilters()
  );

  const { data: transfers = [], isLoading, error } = useQuery({
    queryKey: ["ministerTransfers", filters],
    queryFn: () => getMinisterTransfers(filters),
    staleTime: 10 * 60 * 1000,  // 10 min — filter-keyed; each date range cached separately
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  // Aggregate district flow from the results
  const districtFlow = useMemo<TransferDistrictFlow[]>(() => {
    const map = new Map<string, TransferDistrictFlow>();

    transfers.forEach((t) => {
      // Outgoing from district_from
      if (t.district_from_id && t.district_from_name) {
        const existing = map.get(t.district_from_id);
        if (existing) {
          existing.outgoing++;
          existing.net--;
        } else {
          map.set(t.district_from_id, {
            district_id: t.district_from_id,
            district_name: t.district_from_name,
            incoming: 0,
            outgoing: 1,
            net: -1,
          });
        }
      }
      // Incoming to district_to
      if (t.district_to_id && t.district_to_name) {
        const existing = map.get(t.district_to_id);
        if (existing) {
          existing.incoming++;
          existing.net++;
        } else {
          map.set(t.district_to_id, {
            district_id: t.district_to_id,
            district_name: t.district_to_name,
            incoming: 1,
            outgoing: 0,
            net: 1,
          });
        }
      }
    });

    return Array.from(map.values()).sort(
      (a, b) => b.incoming + b.outgoing - (a.incoming + a.outgoing)
    );
  }, [transfers]);

  const updateFilter = (key: keyof MinisterTransferFilters, value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value }));

  const resetFilters = () => setFilters(getDefaultTransferFilters());

  return {
    transfers,
    districtFlow,
    filters,
    isLoading,
    error,
    updateFilter,
    resetFilters,
  };
}
